import React, { useCallback, useEffect, useState } from 'react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Plus, FileText, AlertTriangle } from 'lucide-react';
import { accountingAPI } from '@/lib/api';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { isKRSuperAdmin } from '@/lib/utils';
import { ADJUSTMENT_STATUS_META, ADJUSTMENT_TYPE_META, RECIPIENT_TYPE_LABEL, money, fmtDate, TOASTS } from '@/lib/accountingConstants';
import EmptyState from '@/components/accounting/EmptyState';
import TableSkeleton from '@/components/accounting/TableSkeleton';
import NewAdjustmentDialog from '@/components/accounting/NewAdjustmentDialog';
import AdjustmentDetailDrawer from '@/components/accounting/AdjustmentDetailDrawer';

const FILTERS_STATUS = [null, 'draft', 'pending_approval', 'posted', 'cancelled'];
const FILTERS_TYPE = [null, 'manual_payment', 'manual_credit', 'manual_debit', 'manual_recovery'];

export default function ManualAdjustmentsListPage() {
  const { user } = useAuth();
  const isKR = isKRSuperAdmin(user);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [typeFilter, setTypeFilter] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [selected, setSelected] = useState(null); // adjustment_id

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (status) params.status = status;
      if (typeFilter) params.adjustment_type = typeFilter;
      const { data } = await accountingAPI.listAdjustments(params);
      setRows(data.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load adjustments');
    } finally { setLoading(false); }
  }, [status, typeFilter]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-6 space-y-6" data-testid="manual-adjustments-page">
      <PageHeader
        title="Manual Adjustments"
        description="Manual payments, credits, debits and recoveries — outside the normal statement flow. Every posted adjustment writes an immutable ledger entry."
        breadcrumbs={[{ label: 'Accounting', to: '/accounting' }, { label: 'Manual Adjustments' }]}
        actions={
          <Button onClick={() => setShowNew(true)} data-testid="new-adjustment-btn">
            <Plus className="h-4 w-4 mr-2" /> New Adjustment
          </Button>
        }
      />

      {/* Filter chips */}
      <div className="space-y-2">
        <div className="flex flex-wrap gap-2">
          <span className="text-xs uppercase tracking-wide text-slate-500 self-center mr-1">Status:</span>
          {FILTERS_STATUS.map((f) => (
            <button key={f || 'all'} onClick={() => setStatus(f)}
              className={`text-xs px-3 py-1.5 rounded-full border transition ${status === f ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'}`}
              data-testid={`status-filter-${f || 'all'}`}>
              {f ? ADJUSTMENT_STATUS_META[f].label : 'All'}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap gap-2">
          <span className="text-xs uppercase tracking-wide text-slate-500 self-center mr-1">Type:</span>
          {FILTERS_TYPE.map((f) => (
            <button key={f || 'all'} onClick={() => setTypeFilter(f)}
              className={`text-xs px-3 py-1.5 rounded-full border transition ${typeFilter === f ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'}`}
              data-testid={`type-filter-${f || 'all'}`}>
              {f ? ADJUSTMENT_TYPE_META[f].label : 'All'}
            </button>
          ))}
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <TableSkeleton rows={5} cols={7} />
          ) : rows.length === 0 ? (
            <EmptyState
              icon={FileText}
              title="No Manual Adjustments"
              description={status || typeFilter ? 'No adjustments match the current filters.' : 'Manual payments, credits, debits and recoveries appear here. Click New Adjustment to create one.'}
              action={<Button size="sm" onClick={() => setShowNew(true)}><Plus className="h-4 w-4 mr-2" /> New Adjustment</Button>}
              dataTestId="adjustments-empty-state"
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200 text-slate-600 sticky top-0 z-10">
                  <tr>
                    <th className="text-left px-4 py-3 font-medium">Adjustment #</th>
                    <th className="text-left px-4 py-3 font-medium">Type</th>
                    <th className="text-left px-4 py-3 font-medium">Recipient</th>
                    <th className="text-right px-4 py-3 font-medium">Amount</th>
                    <th className="text-left px-4 py-3 font-medium">Reason</th>
                    <th className="text-left px-4 py-3 font-medium">Status</th>
                    <th className="text-left px-4 py-3 font-medium">Created</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((a) => {
                    const sMeta = ADJUSTMENT_STATUS_META[a.status] || ADJUSTMENT_STATUS_META.draft;
                    const tMeta = ADJUSTMENT_TYPE_META[a.adjustment_type] || { label: a.adjustment_type, tone: 'bg-slate-100 text-slate-700 border-slate-300' };
                    const SIcon = sMeta.icon;
                    return (
                      <tr
                        key={a.adjustment_id}
                        onClick={() => setSelected(a.adjustment_id)}
                        className="cursor-pointer border-b border-slate-100 hover:bg-slate-50"
                        data-testid={`adj-row-${a.adjustment_id}`}
                      >
                        <td className="px-4 py-3 font-mono text-xs">{a.adjustment_number}</td>
                        <td className="px-4 py-3"><Badge className={`${tMeta.tone} border font-normal`}>{tMeta.label}</Badge></td>
                        <td className="px-4 py-3">
                          <div className="font-medium text-slate-800">{a.recipient_name || a.recipient_id}</div>
                          <div className="text-xs uppercase text-slate-500">{RECIPIENT_TYPE_LABEL[a.recipient_type] || a.recipient_type}</div>
                        </td>
                        <td className="px-4 py-3 text-right font-medium">
                          {money(a.amount)}
                          {a.recipient_blocked && (
                            <div className="flex items-center justify-end gap-1 text-[10px] text-amber-700 mt-0.5">
                              <AlertTriangle className="h-3 w-3" /> Blocked recipient
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-slate-700">{a.reason}</td>
                        <td className="px-4 py-3">
                          <Badge className={`${sMeta.tone} border font-normal`}>
                            {SIcon && <SIcon className="h-3 w-3 mr-1 inline" />} {sMeta.label}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-xs text-slate-600">
                          {fmtDate(a.created_at)}<br/><span className="text-slate-500">{a.created_by_name}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <NewAdjustmentDialog
        open={showNew}
        onClose={() => setShowNew(false)}
        onCreated={(adj) => {
          setShowNew(false);
          setSelected(adj.adjustment_id);
          toast.success(TOASTS.ADJUSTMENT_CREATED);
          load();
        }}
      />

      {selected && (
        <AdjustmentDetailDrawer
          adjustmentId={selected}
          isKR={isKR}
          onClose={() => { setSelected(null); load(); }}
        />
      )}
    </div>
  );
}
